import React, { FC } from 'react';
import { TAdvisorProps } from './types/advisor.types';
import LazySection from '../@shared/lazySection';
import { AdvisorModel } from './model/advisor.model';
import { documentService } from '../../utils/window.utils';
import selectors from './constants/selectors';
import LoadAdvisor from './components/loadAdvisor';

const Advisor: FC<TAdvisorProps> = (props) => {
  const model = new AdvisorModel(props);

  let currentSlide = 0;
  let radioButtons: NodeList | undefined = undefined;

  const getCurrentSlideNumber = () => {
    let number = 0; //last slide is not in slider
    const slides = documentService().querySelectorAll(selectors.slides);
    slides.forEach((slide, index) => {
      if (slide.classList.contains('xc_slide-container--current')) {
        number = index + 1;
      }
    });
    return slides.length > 0 ? number : 6;
  };

  const getCurrentSlide = (): HTMLElement => {
    let currentSlideItem;
    const slides = documentService().querySelectorAll(selectors.slides);
    slides.forEach((slide) => {
      if (slide.classList.contains('xc_slide-container--current')) {
        currentSlideItem = slide;
      }
    });
    return slides.length > 0 ? currentSlideItem : documentService().querySelector(selectors.lastSlide);
  };

  const waitSliderToUpdate = (): Promise<void> =>
    new Promise((resolve) => {
      const lastSlide = getCurrentSlide();
      const slidesObserver = new MutationObserver(() => {
        if (getCurrentSlide() !== lastSlide) resolve();
        if (documentService().querySelectorAll(selectors.slides).length < 1) resolve();
      });
      const config = { attributes: true, childList: true, subtree: true };
      slidesObserver.observe(documentService().querySelector(selectors.slides).parentElement, config);
    });

  const waitPowerRangeSlideToUpdate = (): Promise<void> =>
    new Promise((resolve) => {
      const slideObserver = new MutationObserver(() => {
        if (documentService().querySelector(selectors.powerRangeRadioButtonsWrapper)) resolve();
      });
      const config = { attributes: true, childList: true, subtree: true };
      slideObserver.observe(getCurrentSlide(), config);
    });

  // TO DO: Not used! Check if this function is needed.
  // const appendPagination = () => {
  //   currentSlide = getCurrentSlideNumber();
  //   let pagination: HTMLElement = document.createElement('div');
  //   pagination.className = 'xc_pagination';
  //   pagination.innerHTML = `
  //           <div id="${selectors.paginationBackButtonId}" class="xc_pagination-back-button ${currentSlide < 2 ? "xc_pagination-back-button--hidden" : ""}">
  //               <pfe-icon icon="dcx-arrow-i-os-backward" size="16" ></pfe-icon>
  //           </div>
  //           <div class="xc_pagination-bar">
  //               <div id="${selectors.paginationBarId}" class="xc_pagination-bar-line xc_pagination-bar-line-${currentSlide}">
  //               </div>
  //           </div>
  //           <div class="xc_pagination-counter">
  //               <span id="${selectors.paginationCurrentSlideId}">${currentSlide}</span>/${slideCount}
  //           </div>
  //       `;
  //   const wrapper: HTMLElement = document.querySelector(selectors.wrapper);
  //   wrapper?.appendChild(pagination);
  // }
  // check log for: const appendPagination = () => {

  const removePagination = (): void => {
    documentService().getElementById(selectors.paginationBarId)?.parentElement?.parentElement?.remove();
  };

  const addResetButton = (): void => {
    if (!documentService().getElementById(selectors.restartButtonId)) {
      const restartButtonCopy: HTMLElement = documentService().querySelector(selectors.restartButton);
      const restartButton: HTMLElement = documentService().createElement('button');
      restartButton.className = 'xc_restart-button';
      restartButton.innerHTML = '<pfe-icon icon="dcx-arrow-i-os-backward" size="18" style="padding-bottom:2px"></pfe-icon> Restart';
      const currentSlideContainer = documentService().querySelector(selectors.sliderWrapper);
      currentSlideContainer && currentSlideContainer.appendChild(restartButton);
      restartButton.addEventListener('click', () => {
        restartButtonCopy.click();
      });
    }
  };

  const updatePagination = (): void => {
    currentSlide = getCurrentSlideNumber();
    documentService().getElementById(selectors.paginationBackButtonId).className = `xc_pagination-back-button ${
      currentSlide < 2 ? 'xc_pagination-back-button--hidden' : ''
    }`;
    documentService().getElementById(selectors.paginationBarId).className = `xc_pagination-bar-line xc_pagination-bar-line-${currentSlide}`;
    documentService().getElementById(selectors.paginationCurrentSlideId).innerHTML = currentSlide.toString();
  };

  const appendImage = (): void => {
    const image: HTMLElement = documentService().createElement('div');
    image.className = 'xc_featured-image-wrapper';
    image.innerHTML = `<img loading="lazy" class="xc_featured-image" src="${model.featuredImage?.value?.src}" alt="${model.featuredImage?.value?.alt}"/>`;
    const currentSlideContainer = getCurrentSlide()?.firstChild?.firstChild;
    if (!currentSlideContainer) return;
    currentSlideContainer.insertBefore(image, currentSlideContainer.firstChild);
  };
  const updateSlideAfterClick = async () => {
    await waitSliderToUpdate();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    updateSlide();
  };
  const nextButtonClick = (): void => {
    const nextButton: HTMLElement = documentService().querySelector(selectors.nextButton);
    nextButton.click();
    updateSlideAfterClick();
  };

  const radioButtonClick = (radioButtonsValue): void => {
    if (!radioButtonsValue?.length) return;
    radioButtonsValue.forEach((button) => {
      button.addEventListener('click', () => {
        nextButtonClick();
      });
    });
  };
  const bindClickOnRadioButtons = (): void => {
    radioButtons = documentService().querySelectorAll(selectors.radioButtons);
    radioButtonClick(radioButtons);
  };

  const bindClickOnWhatAreYouLookingRadioButtons = (): void => {
    radioButtons = documentService().querySelectorAll(selectors.whatAreYouLookingForRadio);
    radioButtonClick(radioButtons);
  };

  const bindClickOnPowerRangeRadioButtons = (): void => {
    radioButtons = documentService().querySelectorAll(selectors.powerRangeRadioButtons);
    if (!radioButtons?.length) return;
    radioButtons.forEach((button) => {
      button.firstChild?.addEventListener('click', () => {
        nextButtonClick();
      });
    });
  };

  const appendProceedButton = (): void => {
    const proceedButton: HTMLElement = documentService().createElement('button');
    proceedButton.className = 'xc_proceed-button';
    proceedButton.innerHTML = `${model?.proceedButtonText?.value}`;
    const currentSlideContainer = getCurrentSlide()?.firstChild?.firstChild?.lastChild;
    currentSlideContainer?.appendChild(proceedButton);
    proceedButton.addEventListener('click', () => {
      nextButtonClick();
    });
  };

  const bindYesButton = (): void => {
    const yesButton = documentService().querySelector(selectors.powerRangeYesButton);
    yesButton.addEventListener('click', async () => {
      await waitPowerRangeSlideToUpdate();
      bindClickOnPowerRangeRadioButtons();
    });
  };

  const bindNewOptionsButton = (): void => {
    documentService()
      .querySelector(selectors.powerRangeNewOptionsButton)
      .addEventListener('click', () => {
        nextButtonClick();
      });
  };
  const updateSlide = (): void => {
    const slideNumber = getCurrentSlideNumber();
    if (slideNumber < 6) {
      updatePagination();
      appendImage();
    }
    if (slideNumber === 1 || slideNumber === 2) {
      bindClickOnRadioButtons();
    }
    if (slideNumber === 3) {
      appendProceedButton();
    }
    if (slideNumber === 4) {
      bindYesButton();
      bindNewOptionsButton();
      if (documentService().querySelector(selectors.powerRangeRadioButtonsWrapper)) {
        bindClickOnPowerRangeRadioButtons();
      }
    }
    if (slideNumber === 5) {
      bindClickOnWhatAreYouLookingRadioButtons();
    }
    if (slideNumber === 6) {
      removePagination();
      addResetButton();
    }
  };

  return (
    <LazySection>
      <div className="advisor-wrapper">
        <LoadAdvisor scriptUrl={model.scriptUrl} />
      </div>
    </LazySection>
  );
};

export default Advisor;
